import React, { useState, useEffect } from 'react';
import { Dialog, DialogTitle, DialogContent, IconButton, CircularProgress } from '@material-ui/core';
import { Close as CloseIcon, CheckCircle } from '@material-ui/icons';
import { DropboxFileBrowser } from '../../Pages/cloudUpload/components/DropboxFileBrowser';
import { GoogleDriveFileBrowser } from '../../Pages/cloudUpload/components/GoogleDriveFileBrowser';
import styles from './CloudUploadModalClassic.module.css';

// Dropbox SVG Icon
const DropboxIcon = () => (
  <svg width="32" height="32" viewBox="0 0 24 24">
    <path d="M6 1.807L0 5.629l6 3.822 6.001-3.822L6 1.807zM18 1.807l-6 3.822 6 3.822 6-3.822-6-3.822zM0 13.274l6 3.822 6.001-3.822L6 9.452l-6 3.822zM18 9.452l-6 3.822 6 3.822 6-3.822-6-3.822zM6 18.371l6.001 3.822 6-3.822-6-3.822L6 18.371z" fill="#0061FF" />
  </svg>
);

// Google Drive SVG Icon
const GoogleDriveIcon = () => (
  <svg width="32" height="32" viewBox="0 0 87.3 78" fill="currentColor">
    <path d="m6.6 66.85 3.85 6.65c.8 1.4 1.95 2.5 3.3 3.3l13.75-23.8h-27.5c0 1.55.4 3.1 1.2 4.5z" fill="#0066da" />
    <path d="m43.65 25-13.75-23.8c-1.35.8-2.5 1.9-3.3 3.3l-25.4 44a9.06 9.06 0 0 0 -1.2 4.5h27.5z" fill="#00ac47" />
    <path d="m73.55 76.8c1.35-.8 2.5-1.9 3.3-3.3l1.6-2.75 7.65-13.25c.8-1.4 1.2-2.95 1.2-4.5h-27.502l5.852 11.5z" fill="#ea4335" />
    <path d="m43.65 25 13.75-23.8c-1.35-.8-2.9-1.2-4.5-1.2h-18.5c-1.6 0-3.15.45-4.5 1.2z" fill="#00832d" />
    <path d="m59.8 53h-32.3l-13.75 23.8c1.35.8 2.9 1.2 4.5 1.2h50.8c1.6 0 3.15-.45 4.5-1.2z" fill="#2684fc" />
    <path d="m73.4 26.5-12.7-22c-.8-1.4-1.95-2.5-3.3-3.3l-13.75 23.8 16.15 28h27.45c0-1.55-.4-3.1-1.2-4.5z" fill="#ffba00" />
  </svg>
);

const CloudUploadModalClassic = ({
  open,
  onClose,
  service,
  onFilesSelected,
  isUploading = false,
  onShowMessage,
}) => {
  const [isConnected, setIsConnected] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const [connectionData, setConnectionData] = useState(null);
  const [showFileBrowser, setShowFileBrowser] = useState(false);

  // Track all opened popups so we can close them all when one completes
  const popupsRef = React.useRef([]);
  const storageListenerRef = React.useRef(null);

  // Cleanup popups and listener when modal closes
  useEffect(() => {
    if (!open) {
      // Close all popups when modal closes
      popupsRef.current.forEach((p) => {
        try {
          if (p && !p.closed) {
            p.close();
          }
        } catch (e) {
          // Ignore errors
        }
      });
      popupsRef.current = [];

      // Remove storage listener
      if (storageListenerRef.current) {
        window.removeEventListener('storage', storageListenerRef.current);
        storageListenerRef.current = null;
      }
    }
  }, [open]);

  useEffect(() => {
    if (open && service) {
      // Check if already connected
      const saved = localStorage.getItem('cloudConnectedServices');
      if (saved) {
        const services = JSON.parse(saved);
        if (services[service]) {
          setIsConnected(true);
          setConnectionData(services[service]);
          // Automatically open file browser if already connected
          setShowFileBrowser(true);
        } else {
          // Not connected, reset state
          setIsConnected(false);
          setConnectionData(null);
          setShowFileBrowser(false);
        }
      } else {
        // No saved services, reset state
        setIsConnected(false);
        setConnectionData(null);
        setShowFileBrowser(false);
      }
    } else {
      // Reset state when modal closes
      setIsConnected(false);
      setConnectionData(null);
      setShowFileBrowser(false);
      setIsConnecting(false);
    }
  }, [open, service]);

  const handleConnect = async () => {
    setIsConnecting(true);

    const redirectUri = `${window.location.origin}/cloud-upload`;
    // Encode service in state so popup can identify which service authenticated
    const state = `${service}_${Math.random().toString(36).substring(7)}`;
    let authUrl = '';

    if (service === 'dropbox') {
      const clientId = 'xycymyua8j9yykw';
      sessionStorage.setItem('dropboxOAuthState', state);
      authUrl = `https://www.dropbox.com/oauth2/authorize?client_id=${clientId}&response_type=token&redirect_uri=${encodeURIComponent(redirectUri)}&state=${state}&force_reapprove=true&force_reauthentication=true`;
    } else if (service === 'googledrive') {
      const clientId = '526946985013-7ol8pss03r4cd6dqbijso7a4pfrgnhsi.apps.googleusercontent.com';
      const scope = 'https://www.googleapis.com/auth/drive.readonly https://www.googleapis.com/auth/userinfo.email';
      sessionStorage.setItem('googleDriveOAuthState', state);
      authUrl = `https://accounts.google.com/o/oauth2/v2/auth?client_id=${clientId}&redirect_uri=${encodeURIComponent(redirectUri)}&response_type=token&scope=${encodeURIComponent(scope)}&state=${state}&include_granted_scopes=true`;
    }

    // Open OAuth in a popup window instead of redirecting the main page
    // This prevents ongoing uploads from being interrupted
    const popupWidth = 600;
    const popupHeight = 700;
    const left = window.screenX + (window.outerWidth - popupWidth) / 2;
    const top = window.screenY + (window.outerHeight - popupHeight) / 2;

    // Set flag before opening popup so CloudUpload knows it's a popup flow
    // Use localStorage instead of sessionStorage because sessionStorage is not shared between tabs
    localStorage.setItem('isOAuthPopup', 'true');
    console.log('DEBUG_PARENT::: Opening OAuth popup', { service, authUrl: `${authUrl.substring(0, 50)}...` });

    // Use unique name so multiple popups can be opened (user might click multiple times)
    const popupName = `oauth_popup_${Date.now()}`;
    const popup = window.open(
      authUrl,
      popupName,
      `width=${popupWidth},height=${popupHeight},left=${left},top=${top},scrollbars=yes,resizable=yes`
    );

    // Track this popup
    if (popup) {
      popupsRef.current.push(popup);
    }
    console.log('DEBUG_PARENT::: Popup opened', { popup: !!popup, totalPopups: popupsRef.current.length });

    // Helper to close all popups
    const closeAllPopups = () => {
      popupsRef.current.forEach((p) => {
        try {
          if (p && !p.closed) {
            p.close();
          }
        } catch (e) {
          // Ignore errors closing popups
        }
      });
      popupsRef.current = [];
    };

    // Only add listener once (not for each popup)
    if (!storageListenerRef.current) {
      const handleStorageChange = (event) => {
        console.log('DEBUG_PARENT::: Storage event received', { key: event.key, newValue: !!event.newValue });

        if (event.key === 'oauthPopupComplete' && event.newValue) {
          const { service: completedService, accessToken } = JSON.parse(event.newValue);
          console.log('DEBUG_PARENT::: OAuth complete signal received', { completedService, hasToken: !!accessToken });

          if (completedService === service && accessToken) {
            // Clean up listener
            window.removeEventListener('storage', handleStorageChange);
            storageListenerRef.current = null;
            localStorage.removeItem('oauthPopupComplete');
            localStorage.removeItem('isOAuthPopup');

            // Close ALL popups (user might have opened multiple)
            closeAllPopups();

            // Read connection data from localStorage
            const saved = localStorage.getItem('cloudConnectedServices');
            const services = saved ? JSON.parse(saved) : {};

            if (services[service]) {
              console.log('DEBUG_PARENT::: Updating state with connection data');
              setIsConnected(true);
              setConnectionData(services[service]);
              setShowFileBrowser(true);
            }
            setIsConnecting(false);
          }
        }
      };

      window.addEventListener('storage', handleStorageChange);
      storageListenerRef.current = handleStorageChange;

      // Timeout after 5 minutes - clean up listener and popups
      setTimeout(() => {
        if (storageListenerRef.current) {
          window.removeEventListener('storage', storageListenerRef.current);
          storageListenerRef.current = null;
        }
        localStorage.removeItem('isOAuthPopup');
        closeAllPopups();
        setIsConnecting(false);
      }, 300000);
    }
  };

  const getServiceName = () => (service === 'dropbox' ? 'Dropbox' : 'Google Drive');

  const getServiceColor = () => (service === 'dropbox' ? '#0061FF' : '#4285F4');

  const getServiceIcon = () => (service === 'dropbox' ? <DropboxIcon /> : <GoogleDriveIcon />);

  const handleFilesSelected = (files) => {
    if (onFilesSelected && files.length > 0) {
      onFilesSelected(files, service);
    }
    // Close the modal after files are selected
    onClose();
  };

  const handleFileBrowserClose = () => {
    // Don't close if uploading
    if (isUploading) return;

    setShowFileBrowser(false);
    // Also close the main modal when file browser is closed
    onClose();
  };

  // Handle token expired - clear connection and show connect modal
  const handleTokenExpired = () => {
    // Remove the expired token from localStorage
    const saved = localStorage.getItem('cloudConnectedServices');
    if (saved) {
      const services = JSON.parse(saved);
      services[service] = null;
      localStorage.setItem('cloudConnectedServices', JSON.stringify(services));
    }

    // Reset local state to show connect modal
    setIsConnected(false);
    setConnectionData(null);
    setShowFileBrowser(false);

    // Show message to user
    if (onShowMessage) {
      onShowMessage(`${getServiceName()} session expired. Please reconnect.`);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={isUploading ? undefined : onClose}
      maxWidth="sm"
      fullWidth
      classes={{ paper: styles.dialogPaper }}
    >
      <DialogTitle className={styles.dialogTitle}>
        <div className={styles.titleContent}>
          <div className={styles.serviceIcon} style={{ color: getServiceColor() }}>
            {getServiceIcon()}
          </div>
          <span>
            Import from
            {' '}
            {getServiceName()}
          </span>
          {isUploading && (
            <span style={{ marginLeft: '8px', fontSize: '14px', color: '#666' }}>
              (Uploading...)
            </span>
          )}
        </div>
        <IconButton onClick={onClose} className={styles.closeButton} disabled={isUploading}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      {!showFileBrowser && (
        <DialogContent className={styles.dialogContent}>
          {!isConnected && (
            <div className={styles.connectSection}>
              <div className={styles.connectIcon} style={{ color: getServiceColor() }}>
                {getServiceIcon()}
              </div>
              <h3 className={styles.connectTitle}>
                Connect to
                {' '}
                {getServiceName()}
              </h3>
              <p className={styles.connectDescription}>
                Connect your
                {' '}
                {getServiceName()}
                {' '}
                account to import photos and videos
              </p>
              <button
                className={styles.connectButton}
                onClick={handleConnect}
                disabled={isConnecting}
                style={{ backgroundColor: getServiceColor() }}
              >
                {isConnecting ? (
                  <>
                    <CircularProgress size={20} style={{ color: '#fff', marginRight: '8px' }} />
                    Connecting...
                  </>
                ) : (
                  <>
                    <CheckCircle style={{ marginRight: '8px' }} />
                    Connect
                    {' '}
                    {getServiceName()}
                  </>
                )}
              </button>
            </div>
          )}
        </DialogContent>
      )}

      {/* Dropbox File Browser Modal */}
      {showFileBrowser && service === 'dropbox' && connectionData && (
        <DropboxFileBrowser
          accessToken={connectionData.accessToken}
          onClose={handleFileBrowserClose}
          onFilesSelected={handleFilesSelected}
          onShowMessage={onShowMessage}
          onTokenExpired={handleTokenExpired}
        />
      )}

      {/* Google Drive File Browser Modal */}
      {showFileBrowser && service === 'googledrive' && connectionData && (
        <GoogleDriveFileBrowser
          accessToken={connectionData.accessToken}
          onClose={handleFileBrowserClose}
          onFilesSelected={handleFilesSelected}
          onShowMessage={onShowMessage}
          onTokenExpired={handleTokenExpired}
        />
      )}
    </Dialog>
  );
};

export default CloudUploadModalClassic;
